import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { StreamChat } from 'stream-chat';
import { useAuth } from './AuthContext';
import { useUserRole } from '@/hooks/useUserRole';
import { supabase } from '@/integrations/supabase/client';

interface ChatContextType {
  client: StreamChat | null;
  isConnected: boolean;
  isLoading: boolean;
  error: string | null;
  currentChurchId: string | null;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) throw new Error('useChat must be used within a ChatProvider');
  return context;
};

interface ChatProviderProps {
  children: ReactNode;
}

export const ChatProvider = ({ children }: ChatProviderProps) => {
  console.log('🔍 ChatProvider - Component started');
  
  const { user, session } = useAuth();
  
  console.log('🔍 ChatProvider - Calling useUserRole...');
  const { roles = [], isSuperAdmin = false, loading: rolesLoading = false } = useUserRole();
  console.log('✅ ChatProvider - useUserRole completed:', { rolesLength: roles.length, isSuperAdmin, rolesLoading });
  const [client, setClient] = useState<StreamChat | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentChurchId, setCurrentChurchId] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);

  // --- Safe helper -----------------------------------------------------------
  const getValidChurchRole = (list: any[]) =>
    Array.isArray(list)
      ? list.find((r) => r && r.role && r.role !== 'super_admin')
      : null;
  // ---------------------------------------------------------------------------

  // Get current church ID from user roles
  useEffect(() => {
    // Don't process roles while they're still loading
    if (rolesLoading) {
      console.log('⏳ ChatContext - Roles still loading, skipping processing');
      return;
    }

    try {
      console.group('🔍 ChatContext - Processing roles update');
      console.log('📊 Roles received:', roles);
      console.log('📊 Roles type:', typeof roles, 'isArray:', Array.isArray(roles));
      console.log('📊 Roles length:', Array.isArray(roles) ? roles.length : 'not array');
      console.log('👑 Is super admin:', isSuperAdmin);
      
      console.log('🔍 Condition check: Array.isArray(roles) =', Array.isArray(roles));
      console.log('🔍 Condition check: roles.length > 0 =', Array.isArray(roles) ? roles.length > 0 : false);
      console.log('🔍 Condition check: !isSuperAdmin =', !isSuperAdmin);

      if (Array.isArray(roles) && roles.length > 0) {
        if (isSuperAdmin) {
          console.log('👑 Super admin detected - setting first available church as currentChurchId');
          // For super admins, use the first church they have access to
          const firstRole = roles.find(role => role.church_id && role.church_id !== '00000000-0000-0000-0000-000000000000');
          if (firstRole?.church_id) {
            console.log('✅ Setting currentChurchId for super admin to:', firstRole.church_id);
            setCurrentChurchId(firstRole.church_id);
          } else {
            console.log('⚠️ Super admin but no valid church ID found');
            setCurrentChurchId(null);
          }
        } else {
          console.log('✅ Regular user - looking for church role...');
          const churchRole = getValidChurchRole(roles);
          console.log('🏛️ Found church role:', churchRole);
          
          if (churchRole?.church_id) {
            console.log('✅ Setting currentChurchId to:', churchRole.church_id);
            setCurrentChurchId(churchRole.church_id);
          } else {
            console.log('❌ No valid church role found in:', roles);
            setCurrentChurchId(null);
          }
        }
      } else {
        console.log('❌ Clearing currentChurchId - no roles available');
        console.log('   - Array.isArray(roles):', Array.isArray(roles));
        console.log('   - roles.length > 0:', Array.isArray(roles) ? roles.length > 0 : false);
        console.log('   - isSuperAdmin:', isSuperAdmin);
        setCurrentChurchId(null);
      }
      console.groupEnd();
    } catch (err) {
      console.error('❌ ChatContext role parsing error:', err);
      setCurrentChurchId(null);
    }
  }, [roles, isSuperAdmin, rolesLoading]);

  const connectUser = async () => {
    if (!user || !session || !currentChurchId || isLoading) return;

    setIsLoading(true);
    setError(null);

    try {
      const apiKey = import.meta.env.VITE_STREAM_API_KEY;
      if (!apiKey) {
        console.warn('Chat disabled: missing Stream API key');
        setError(null); // Don't set error, just silently disable
        setIsLoading(false);
        return;
      }

      const { data, error } = await supabase.functions.invoke('stream-token', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (error) {
        console.warn('Stream token error:', error);
        setError(null); // Don't block the app, just disable chat
        setIsLoading(false);
        return;
      }

      const { token, userId, name } = data || {};
      if (!token || !userId) {
        console.warn('Invalid Stream token payload');
        setError(null);
        setIsLoading(false);
        return;
      }

      const streamClient = StreamChat.getInstance(apiKey);
      await streamClient.connectUser(
        {
          id: userId,
          name: name ?? user.email ?? 'Unknown User',
          image: user.user_metadata?.avatar_url,
        },
        token
      );

      setClient(streamClient);
      setIsConnected(true);
      console.log('Stream Chat connected for user:', userId);
    } catch (err: any) {
      const message = err?.message ?? 'Failed to connect to chat';
      console.warn('Chat connection error (non-blocking):', err);
      // Don't set error to block the app - just log it
      setError(null);
    } finally {
      setIsLoading(false);
    }
  };

  const disconnectUser = async () => {
    try {
      if (client) {
        await client.disconnectUser();
        setClient(null);
        setIsConnected(false);
        console.log('Stream Chat disconnected');
      }
    } catch (err) {
      console.error('Chat disconnect error:', err);
    }
  };

  // Connect when user and church are available (including super admins)
  // Prevent infinite remounts with initialization guard
  useEffect(() => {
    // Skip chat init when Stream API key is missing (prevents infinite mounting)
    if (!import.meta.env.VITE_STREAM_API_KEY) return;

    console.log('🔍 ChatContext - connectUser effect:', {
      user: !!user,
      session: !!session,
      currentChurchId,
      isConnected,
      initialized,
      isSuperAdmin,
      rolesLength: roles.length,
      rolesLoading
    });
    
    // Guard: Prevent infinite remounts
    if (!initialized && user && session && currentChurchId && !isConnected && Array.isArray(roles) && !rolesLoading) {
      console.log('✅ ChatContext - triggering connectUser');
      connectUser();
      setInitialized(true);
    } else if (!initialized) {
      console.log('❌ ChatContext - connectUser conditions not met:', {
        hasUser: !!user,
        hasSession: !!session,
        hasChurchId: !!currentChurchId,
        isConnected,
        hasRoles: Array.isArray(roles),
        rolesLoading,
        isSuperAdmin,
        reason: !user ? 'no user' : !session ? 'no session' : !currentChurchId ? 'no churchId' : !Array.isArray(roles) ? 'roles not loaded' : rolesLoading ? 'roles loading' : isConnected ? 'already connected' : 'unknown'
      });
    }
  }, [initialized, user, session, currentChurchId, isConnected, roles, rolesLoading, isSuperAdmin]);

  // Cleanup on unmount or user change
  useEffect(() => {
    return () => {
      if (client) client.disconnectUser().catch(() => {});
    };
  }, [client]);

  // Disconnect when user logs out
  useEffect(() => {
    if (!user && client) disconnectUser();
  }, [user, client]);

  return (
    <ChatContext.Provider
      value={{
        client,
        isConnected,
        isLoading,
        error,
        currentChurchId,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};
